import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { shopifyFetch, PRODUCTS_QUERY, nodes } from "@/lib/shopify";

const HERO_IMG =
  "https://images.unsplash.com/photo-1614943430627-f0a28776c24e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1734600425580-41733b398abd?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const LINES = ["IT'S ALWAYS", "WEIRD O'CLOCK", "SOMEWHERE"];

function WorldClocks() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const fmt = (tz) =>
    new Intl.DateTimeFormat("en-AU", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: tz,
    }).format(now);
  const zones = [
    { label: "TOKYO", tz: "Asia/Tokyo" },
    { label: "NEW YORK", tz: "America/New_York" },
    { label: "PARIS", tz: "Europe/Paris" },
    { label: "WEIRD O'CLOCK", tz: undefined, weird: true },
  ];
  return (
    <div
      data-testid="hero-world-clocks"
      className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6"
    >
      {zones.map((z) => (
        <div key={z.label}>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-ivory/40">
            {z.label}
          </p>
          <p
            className={`font-mono2 text-lg font-bold tabular-nums ${
              z.weird ? "text-amber" : "text-ivory"
            }`}
          >
            {z.tz ? fmt(z.tz) : fmt(undefined)}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    shopifyFetch(PRODUCTS_QUERY, { first: 6, sortKey: "BEST_SELLING" })
      .then((d) =>
        setFeatured(
          nodes(d.products).find((p) => !p.handle.includes("doggy")) ||
            nodes(d.products)[0] ||
            null
        )
      )
      .catch(() => {});
  }, []);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative overflow-hidden bg-void pt-32 pb-16 sm:pt-40 sm:pb-24"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-amber/10 blur-[140px]" />
      <div className="pointer-events-none absolute right-[-200px] top-1/3 h-[400px] w-[400px] rounded-full bg-volt/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 font-mono2 text-xs uppercase tracking-[0.35em] text-amber"
          >
            Curated surreal clocks & eccentric home decor
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-display text-4xl font-black leading-[1.02] tracking-tight text-ivory sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 ? (
                    <span className="font-editorial italic text-amber">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-md text-base leading-relaxed text-ivory/60 sm:text-lg"
          >
            Clocks that melt, mirrors that misbehave, and decor that refuses to be
            ignored. Functional art for homes with a pulse.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/collections/weird-clocks"
              data-testid="hero-shop-clocks-button"
              className="group inline-flex items-center gap-2 rounded-full bg-amber px-7 py-3.5 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(255,92,0,0.35)]"
            >
              Shop Clocks
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/collections/odd-decor"
              data-testid="hero-explore-decor-button"
              className="inline-flex items-center gap-2 rounded-full border border-ivory/25 px-7 py-3.5 font-mono2 text-xs font-bold uppercase tracking-[0.2em] text-ivory transition-all duration-300 hover:border-amber hover:text-amber"
            >
              Explore Odd Decor
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12"
          >
            <WorldClocks />
          </motion.div>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative ml-auto aspect-[3/4] w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/10"
          >
            <img
              src={HERO_IMG}
              alt="Surreal gold clock sculpture in an eclectic interior"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: yCard }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="animate-float-slow absolute -left-10 top-16 w-44 overflow-hidden rounded-xl border border-white/15 bg-black/50 backdrop-blur-xl"
          >
            <img
              src={featured?.featuredImage?.url || HERO_IMG_2}
              alt={featured?.featuredImage?.altText || "Sculptural silver clock in a lounge"}
              className="aspect-square w-full object-cover"
            />
            {featured && (
              <Link
                to={`/products/${featured.handle}`}
                data-testid="hero-featured-product-link"
                className="block px-3 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-ivory/80 hover:text-amber"
              >
                {featured.title}
              </Link>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="absolute -bottom-6 right-6 rounded-full border border-amber/40 bg-void/80 px-5 py-2.5 backdrop-blur-xl"
          >
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber">
              Est. anti-boring
            </span>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="relative mx-auto mt-16 flex max-w-[1400px] items-center gap-3 px-5 sm:px-8"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-amber" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-ivory/40">
          Scroll into the strange
        </span>
      </motion.div>
    </section>
  );
}
